import { DetailsColumn, DetailsColumnBase } from "@fluentui/react";
import React from "react";
import { IconName } from "../../../../styles/icons";
import { ActionButton } from "../../../Buttons/ActionButton/ActionButton";
import { Button } from "../../../Buttons/Button/Button";
import { IconButton } from "../../../Buttons/IconButton/IconButton";
import { MenuIconButtonProps } from "../../../Buttons/MenuIconButton/MenuIconButton";
import { Callout } from "../../../Callout/Callout";
import { ErrorCallout } from "../../../ErrorCallout/ErrorCallout";
import { Icon } from "../../../Icon/Icon";
import { Hyperlink } from "../../../Links/Hyperlink/Hyperlink";
import { NoHrefLink } from "../../../Links/NoHrefLink/NoHrefLink";
import { Persona } from "../../../Persona/Persona";
import { Text } from "../../../Text/Text";
import { Toggle } from "../../../Toggle/Toggle";
import { CalloutColumn } from "../../ListItemColumns/CalloutColumn/CalloutColumn";
import { CustomColumn } from "../../ListItemColumns/CustomColumn/CustomColumn";
import { EmptyColumn } from "../../ListItemColumns/EmptyColumn/EmptyColumn";
import { ActionButtonFinalColumn } from "../../ListItemColumns/FinalColumns/ActionButtonFinalColumn/ActionButtonFinalColumn";
import { ActionMenuFinalColumn } from "../../ListItemColumns/FinalColumns/ActionMenuFinalColumn/ActionMenuFinalColumn";
import { ButtonFinalColumn } from "../../ListItemColumns/FinalColumns/ButtonFinalColumn/ButtonFinalColumn";
import { TextFinalColumn } from "../../ListItemColumns/FinalColumns/TextFinalColumn/TextFinalColumn";
import { ToggleFinalColumn } from "../../ListItemColumns/FinalColumns/ToggleFinalColumn/ToggleFinalColumn";
import { FooterColumn } from "../../ListItemColumns/FooterColumn/FooterColumn";
import { HyperlinkColumn } from "../../ListItemColumns/HyperlinkColumn/HyperlinkColumn";
import { IconColumn } from "../../ListItemColumns/IconColumn/IconColumn";
import { SensitiveContentColumn } from "../../ListItemColumns/SensitiveContentColumn/SensitiveContentColumn";
import { CircleShimmerColumn } from "../../ListItemColumns/ShimmerColumns/CircleShimmerColumn/CircleShimmerColumn";
import { LineShimmerColumn } from "../../ListItemColumns/ShimmerColumns/LineShimmerColumn/LineShimmerColumn";
import { MediaShimmerColumn } from "../../ListItemColumns/ShimmerColumns/MediaShimmerColumn/MediaShimmerColumn";
import { PersonaShimmerColumn } from "../../ListItemColumns/ShimmerColumns/PersonaShimmerColumn/PersonaShimmerColumn";
import { TextColumn } from "../../ListItemColumns/TextColumn/TextColumn";
import { HyperlinkTitleColumn } from "../../ListItemColumns/TitleColumns/HyperlinkTitleColumn/HyperlinkTitleColumn";
import { MediaTitleColumn } from "../../ListItemColumns/TitleColumns/MediaTitleColumn/MediaTitleColumn";
import { PersonaTitleColumn } from "../../ListItemColumns/TitleColumns/PersonaTitleColumn/PersonaTitleColumn";
import { TextTitleColumn } from "../../ListItemColumns/TitleColumns/TextTitleColumn/TextTitleColumn";
import { InternalListItemPersonaReflow } from "../blocks/InternalListItemPersonaReflow/InternalListItemPersonaReflow";
import { ListItemMedia } from "../blocks/ListItemMedia/ListItemMedia";
import { ListItemCanvasExternalProps } from "./ListItemCanvas";

export type FinalColumnAlign = "end" | "start";

export interface MultiColumnListItemSharedBaseProps extends ListItemCanvasExternalProps {
    /**
     * Component for the 1st Column
     */
    readonly firstColumn: readonly JSX.Element[];
    /**
     * Optional component for the 2nd Column
     */
    readonly secondColumn?: readonly JSX.Element[];
    /**
     * Optional component for the 3rd Column
     */
    readonly thirdColumn?: readonly JSX.Element[];
    /**
     * Optional text components for the final Column
     */
    readonly finalColumnText?: readonly JSX.Element[];
    /**
     * Optional action components for the final Column
     */
    readonly finalColumnActions?: readonly JSX.Element[];
    /**
     * Whether to align the final Column's components at the end or start of the
     * Column
     */
    readonly finalColumnAlign?: FinalColumnAlign;

    /** Optional flag to indicate that the list item's contents are disabled */
    readonly disabled?: boolean;

    /** Optional flag to indicate that the list item's menu is disabled */
    readonly menuDisabled?: boolean;
}

export interface DefaultMultiColumnListItemProps {
    /**
     * ListItem decorationType discriminant - if no discriminant is provided,
     * it defaults to "none"
     */
    readonly decorationType?: "none";
}

export interface IconMultiColumnListItemProps {
    /**
     * ListItem decorationType discriminant - "icon" accepts an iconName, which will
     * result in an icon rendered on the far right (LTR) of the list
     */
    readonly decorationType: "icon";

    /**
     * Optional iconName, rendered at the far right of the List (LTR)
     */
    readonly iconName: IconName;
}

export interface MenuMultiColumnListItemProps {
    /**
     * ListItem decorationType discriminant - "menu" exposes props for a
     * MenuIconButton that renders on the far right of the list (LTR)
     */
    readonly decorationType: "menu";
    /**
     * MenuIconButton props for this ListItem flavor
     */
    readonly menuProps: MenuIconButtonProps;
}

export type ListItemWithIconProps = IconMultiColumnListItemProps | MenuMultiColumnListItemProps | DefaultMultiColumnListItemProps;

export type MultiColumnListItemSharedProps = ListItemWithIconProps & MultiColumnListItemSharedBaseProps;

// LISTITEM COLUMN VALID COMPONENTS

export const validCenterColumnComponents: readonly React.ComponentType<any>[] = [
    Callout,
    Icon,
    Text,
    Hyperlink,
    ErrorCallout
];

export const validFirstColumnComponents: readonly React.ComponentType<any>[] = [
    InternalListItemPersonaReflow,
    ListItemMedia,
    Persona,
    Text,
    ...validCenterColumnComponents
];

export const validFinalColumnActionComponents: readonly React.ComponentType<any>[] = [
    ActionButton,
    Button,
    Callout,
    IconButton,
    NoHrefLink,
    Hyperlink,
    Toggle
];

export const validColumnComponents: readonly React.ComponentType<any>[] = [
    ActionButtonFinalColumn,
    ActionMenuFinalColumn,
    ButtonFinalColumn,
    CalloutColumn,
    CircleShimmerColumn,
    CustomColumn,
    DetailsColumn,
    DetailsColumnBase,
    EmptyColumn,
    FooterColumn,
    HyperlinkColumn,
    HyperlinkTitleColumn,
    IconColumn,
    LineShimmerColumn,
    MediaShimmerColumn,
    MediaTitleColumn,
    SensitiveContentColumn,
    PersonaShimmerColumn,
    PersonaTitleColumn,
    TextColumn,
    TextFinalColumn,
    TextTitleColumn,
    ToggleFinalColumn
];

export const interactiveColumnComponents: readonly React.ComponentType<any>[] = [
    ActionButtonFinalColumn,
    ActionMenuFinalColumn,
    ButtonFinalColumn,
    CalloutColumn,
    CustomColumn,
    HyperlinkColumn,
    HyperlinkTitleColumn,
    SensitiveContentColumn,
    ToggleFinalColumn
];
